import React, { useState, useEffect, useCallback } from 'react';
// Inline SVG icons — no external dependency
const ShieldCheckIcon = ({className}: {className?: string}) => <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path fillRule="evenodd" d="M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3v-6.75a3 3 0 00-3-3v-3c0-2.9-2.35-5.25-5.25-5.25zm3.75 8.25v-3a3.75 3.75 0 10-7.5 0v3h7.5z" clipRule="evenodd" /></svg>;
const HandThumbDownIcon = ({className}: {className?: string}) => <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M15.73 5.25h1.035A7.465 7.465 0 0118 9.375a7.465 7.465 0 01-1.235 4.125h-.148c-.806 0-1.534.446-2.031 1.08a9.04 9.04 0 01-2.861 2.4c-.723.384-1.35.956-1.653 1.715a4.499 4.499 0 00-.322 1.672V21a.75.75 0 01-.75.75 2.25 2.25 0 01-2.25-2.25c0-1.152.26-2.243.723-3.218.266-.558-.107-1.282-.725-1.282H3.622c-1.026 0-1.945-.694-2.054-1.715A12.137 12.137 0 011.5 12.25c0-2.848.992-5.464 2.649-7.521C4.537 4.247 5.233 4 5.9 4H9.5c.697 0 1.363.078 2.004.225A6.02 6.02 0 0115.73 5.25z" /></svg>;
const CurrencyDollarIcon = ({className}: {className?: string}) => <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M10.464 8.746c.227-.18.497-.311.786-.394v2.795a2.252 2.252 0 01-.786-.393c-.394-.313-.546-.681-.546-1.004 0-.323.152-.691.546-1.004zM12.75 15.662v-2.824c.347.085.664.228.921.421.427.32.579.686.579.991 0 .305-.152.671-.579.991a2.534 2.534 0 01-.921.42z" /><path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM12.75 6a.75.75 0 00-1.5 0v.816a3.836 3.836 0 00-1.72.756c-.712.566-1.112 1.35-1.112 2.178 0 .829.4 1.612 1.113 2.178.502.4 1.102.647 1.719.756v2.978a2.536 2.536 0 01-.921-.421l-.879-.66a.75.75 0 00-.9 1.2l.879.66c.533.4 1.169.645 1.821.75V18a.75.75 0 001.5 0v-.81a4.124 4.124 0 001.821-.749c.745-.559 1.179-1.344 1.179-2.191 0-.847-.434-1.632-1.179-2.191a4.122 4.122 0 00-1.821-.75V8.354c.29.082.559.213.786.393l.415.33a.75.75 0 00.933-1.175l-.415-.33a3.836 3.836 0 00-1.719-.755V6z" clipRule="evenodd" /></svg>;
const LockClosedIcon = ({className}: {className?: string}) => <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path fillRule="evenodd" d="M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3v-6.75a3 3 0 00-3-3v-3c0-2.9-2.35-5.25-5.25-5.25zm3.75 8.25v-3a3.75 3.75 0 10-7.5 0v3h7.5z" clipRule="evenodd" /></svg>;

export interface CartMandate {
    id: string; // UUID v4 nonce
    merchantId: string;
    merchantName: string;
    originalAmount: number;
    finalAmount: number;
    currency: string;
    items: Array<{ name: string; price: number }>;
    agentReasoning: string;
    promoApplied?: {
        code: string;
        savings: number;
    };
}

interface SuperJarvisPayCardProps {
    mandate: CartMandate;
    onApprove: (mandateId: string, signature: string) => Promise<void>;
    onDecline: (mandateId: string) => void;
    onUndo: (mandateId: string) => void;
}

type VaultState = 'review' | 'signing' | 'approved' | 'undo_window' | 'executed' | 'declined';

/**
 * SuperJarvis Pay: Trust-Driven Zero-Trust Vault UI (Web/Desktop)
 * Implements Phase 1 & 2 UI requirements: 
 * - Hardware Assertion Signing (Mocked Web Crypto)
 * - Agent Reasoning & Promo Sweeps
 * - 30-Second Undo Window
 */
export const SuperJarvisPayCard: React.FC<SuperJarvisPayCardProps> = ({
    mandate,
    onApprove,
    onDecline,
    onUndo
}) => {
    const [vaultState, setVaultState] = useState<VaultState>('review');
    const [undoTimer, setUndoTimer] = useState(30);

    // Resolve currency symbol from ISO code (supports CAD, EUR, GBP, etc.)
    const currencySymbol = (() => {
        if (!mandate?.currency) return '$';
        const symbols: Record<string, string> = { USD: '$', CAD: 'C$', EUR: '\u20AC', GBP: '\u00A3', JPY: '\u00A5', AUD: 'A$' };
        const code = typeof mandate.currency === 'string' ? mandate.currency.toUpperCase() : '';
        return symbols[code] || mandate.currency || '$';
    })();

    // Coerce amounts to numbers — Firestore/AI may return strings after deserialization.
    // Strip any leading currency symbols (e.g. "C$375.0", "$375", "\u20AC300") before parsing.
    const parseAmount = (val: unknown): number => {
        if (typeof val === 'number') return val;
        if (typeof val === 'string') {
            // Remove everything except digits, dots, and commas — handles "C$375.0", "$289", etc.
            const cleaned = val.replace(/[^0-9.,]/g, '').replace(/,/g, '');
            const n = parseFloat(cleaned);
            return isNaN(n) ? 0 : n;
        }
        return 0;
    };
    const finalAmount = parseAmount(mandate?.finalAmount);
    const originalAmount = parseAmount(mandate?.originalAmount) || finalAmount;

    // Safely extract mandate id — guard against non-string or missing id
    const mandateId: string = mandate?.id && typeof mandate.id === 'string' ? mandate.id : '';

    // Real browser-side WebCrypto signing.
    // This is still web-platform scoped (not Secure Enclave attestation), but it
    // removes the fake timer/signature path and produces a verifiable assertion.
    const signMandate = async () => {
        setVaultState('signing');
        try {
            const cryptoApi = globalThis.crypto;
            if (!cryptoApi?.subtle) {
                throw new Error('WebCrypto unavailable in this browser');
            }

            const encoder = new TextEncoder();
            const payload = {
                mandateId,
                merchantId: mandate.merchantId,
                merchantName: mandate.merchantName,
                finalAmount,
                currency: mandate.currency,
                signedAt: new Date().toISOString(),
                origin: globalThis.location?.origin || 'unknown',
            };
            const payloadBytes = encoder.encode(JSON.stringify(payload));
            const key = await cryptoApi.subtle.generateKey(
                {
                    name: 'ECDSA',
                    namedCurve: 'P-256',
                },
                false,
                ['sign']
            );
            const signature = await cryptoApi.subtle.sign(
                {
                    name: 'ECDSA',
                    hash: 'SHA-256',
                },
                key.privateKey,
                payloadBytes
            );

            const signatureB64 = btoa(String.fromCharCode(...new Uint8Array(signature)));
            const assertion = JSON.stringify({
                type: 'webcrypto_ecdsa_p256_sha256',
                payload,
                signature: signatureB64,
            });

            await onApprove(mandateId, assertion);
            setVaultState('undo_window');
        } catch (e) {
            console.error('Hardware signing failed', e);
            setVaultState('review');
        }
    };

    // 30-Second Undo Timer Logic
    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (vaultState === 'undo_window') {
            interval = setInterval(() => {
                setUndoTimer((prev) => {
                    if (prev <= 1) {
                        clearInterval(interval);
                        setVaultState('executed'); // Undo window closed, transaction is locked
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [vaultState]);

    const handleUndo = useCallback(() => {
        onUndo(mandateId);
        setVaultState('declined');
    }, [mandateId, onUndo]);

    const handleDecline = useCallback(() => {
        onDecline(mandateId);
        setVaultState('declined');
    }, [mandateId, onDecline]);

    // Guard: bail out if mandate is missing critical fields (prevents render crashes
    // when pay_request from Synapse/Firestore is incomplete or malformed).
    // - mandate must be a plain object (not a string or primitive)
    // - mandate.id must be a non-empty string
    // - finalAmount must be a positive number
    // Placed after all hooks to satisfy Rules of Hooks.
    if (!mandate || typeof mandate !== 'object' || !mandateId || finalAmount <= 0) {
        return null;
    }

    if (vaultState === 'declined') {
        return (
            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 text-red-200 text-sm">
                <HandThumbDownIcon className="w-5 h-5 inline mr-2" />
                Transaction declined and securely destroyed.
            </div>
        );
    }

    if (vaultState === 'executed') {
        return (
            <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 text-green-200 text-sm flex justify-between items-center">
                <span>
                    <ShieldCheckIcon className="w-5 h-5 inline mr-2" />
                    Payment processed securely at {mandate.merchantName || 'merchant'}.
                </span>
                <button onClick={() => alert(`Receipt for mandate ${mandate.id}\n\nMerchant: ${mandate.merchantName}\nAmount: ${mandate.finalAmount} ${mandate.currency}\nStatus: Executed`)} className="text-xs underline hover:text-white transition-colors">
                    View Receipt
                </button>
            </div>
        );
    }

    // Define inline styles for the total section
    const amountStyles = {
        totalContainer: {
            display: 'flex',
            flexDirection: 'column',
        },
        totalText: {
            color: '#9ca3af',
            fontSize: '13px',
        },
        amountRow: {
            display: 'flex',
            alignItems: 'baseline',
            gap: '8px',
            marginTop: '4px',
        },
        originalAmount: {
            color: '#6b7280',
            fontSize: '18px',
            textDecoration: 'line-through',
        },
        finalAmount: {
            color: '#ffffff',
            fontSize: '32px',
            fontWeight: '300',
            letterSpacing: '-0.5px',
        },
    };

    return (
        <div className="bg-[#0f1115] border border-gray-800 rounded-xl overflow-hidden shadow-2xl max-w-md w-full my-4 font-sans ring-1 ring-white/10">

            {/* Vault Header */}
            <div className="px-5 py-3 border-b border-gray-800 bg-gradient-to-r from-gray-900 to-black flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <LockClosedIcon className="w-4 h-4 text-emerald-400" />
                    <span className="text-xs font-semibold tracking-wider text-gray-300 uppercase">SuperJarvis Pay Vault</span>
                </div>
                <span className="text-xs text-gray-500 font-mono">{mandateId.substring(0, 8)}</span>
            </div>

            <div className="p-6">
                {/* Agent Reasoning Layer (UX Gap 2) */}
                {mandate.agentReasoning && typeof mandate.agentReasoning === 'string' && (
                <div className="bg-blue-900/10 border-l-2 border-blue-500 p-4 rounded-r-lg mb-6">
                    <p className="text-sm text-blue-100 leading-relaxed italic">
                        &quot;{mandate.agentReasoning}&quot;
                    </p>
                </div>
                )}

                {/* Promo Sweep */}
                {mandate.promoApplied && (
                    <div className="bg-emerald-900/20 border border-emerald-500/30 rounded-lg p-3 mb-6 flex items-start gap-4">
                        <div className="bg-emerald-500/20 p-2 rounded-full mt-1">
                            <CurrencyDollarIcon className="h-5 w-5 text-emerald-400" />
                        </div>
                        <div>
                            <div className="text-emerald-400 text-sm font-semibold">SuperJarvis Sweep</div>
                            <div className="text-emerald-200/70 text-xs mt-1">
                                Ghost tested codes & applied {mandate.promoApplied.code}.
                                Saved you {currencySymbol}{(Number(mandate.promoApplied.savings) || 0).toFixed(2)}.
                            </div>
                        </div>
                    </div>
                )}

                {/* Cart Total */}
                <div className="flex justify-between items-end mb-8 mt-2">
                    <div>
                        {originalAmount !== finalAmount && (
                            <div className="text-gray-500 line-through text-sm mb-1">
                                {currencySymbol}{originalAmount.toFixed(2)}
                            </div>
                        )}
                        <div style={amountStyles.amountRow}>
                            <span style={amountStyles.originalAmount}>{currencySymbol}</span>
                            <span style={amountStyles.finalAmount}>{finalAmount.toFixed(2)}</span>
                        </div>
                    </div>
                    <span className="text-xs font-mono text-gray-500 uppercase">{mandate.currency}</span>
                </div>

                {/* Interaction Layer */}
                {vaultState === 'review' && (
        <div className="flex flex-col gap-3">
            <button
                onClick={signMandate}
                className="w-full relative group bg-white text-black font-semibold rounded-lg py-3.5 px-4 overflow-hidden transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
                <span className="relative flex items-center justify-center gap-2">
                    Double-Tap to Approve <span className="text-gray-400 text-xs font-normal ml-1">Hardware Sign</span>
                </span>
            </button>
            <button
                onClick={handleDecline}
                className="w-full py-2.5 text-sm font-medium tracking-wide text-gray-400 hover:text-white transition-colors"
            >
                Decline Mandate
            </button>
        </div>
                )}

                {vaultState === 'signing' && (
        <div className="flex items-center justify-center py-6">
            <div className="animate-pulse flex items-center gap-3 text-emerald-400">
                <ShieldCheckIcon className="w-6 h-6 animate-bounce" />
                <span className="text-sm font-medium tracking-widest uppercase">Verifying Enclave...</span>
            </div>
        </div>
                )}

                {/* The 30 Second Undo Window */}
                {vaultState === 'undo_window' && (
        <div className="flex flex-col items-center gap-3">
            <div className="text-center w-full bg-yellow-900/10 border border-yellow-500/20 rounded-lg p-4">
                <p className="text-sm text-yellow-200 mb-2">
                    Payment Authorized. Execution locks in: <span className="font-mono font-bold text-yellow-400">{undoTimer}s</span>
                </p>
                <button
                    onClick={handleUndo}
                    className="w-full bg-gray-800 hover:bg-gray-700 text-white text-sm font-medium py-2 rounded transition-colors border border-gray-700 hover:border-gray-500"
                >
                    Undo Purchase (Cancel)
                </button>
            </div>
            <p className="text-[10px] text-gray-500 text-center px-4">
                Closing this window will not cancel the payment. Use the Undo button to issue an immediate Stripe reversal.
            </p>
        </div>
                )}

            </div>
        </div>
    );
};

export default SuperJarvisPayCard;
